/**
 * NavItem Component
 * LAYER-SHELL: Unified navigation link
 *
 * Individual navigation link with icon, label, and active state.
 * Supports all portal icon types.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon as HomeIconOutline,
  DocumentTextIcon as DocumentTextIconOutline,
  UserCircleIcon as UserCircleIconOutline,
  Cog6ToothIcon as Cog6ToothIconOutline,
  UsersIcon as UsersIconOutline,
  TagIcon as TagIconOutline,
  ClipboardDocumentListIcon as ClipboardDocumentListIconOutline,
  ChartBarIcon as ChartBarIconOutline,
  ChartBarSquareIcon as ChartBarSquareIconOutline,
  Squares2X2Icon as Squares2X2IconOutline,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconOutline,
  QueueListIcon as QueueListIconOutline,
  ShieldCheckIcon as ShieldCheckIconOutline,
  FolderIcon as FolderIconOutline,
  DocumentMagnifyingGlassIcon as DocumentMagnifyingGlassIconOutline,
  BellIcon as BellIconOutline,
  ShoppingCartIcon as ShoppingCartIconOutline,
  CreditCardIcon as CreditCardIconOutline,
  TruckIcon as TruckIconOutline,
  BuildingOffice2Icon as BuildingOffice2IconOutline,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  UsersIcon as UsersIconSolid,
  TagIcon as TagIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  ChartBarSquareIcon as ChartBarSquareIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  QueueListIcon as QueueListIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  FolderIcon as FolderIconSolid,
  DocumentMagnifyingGlassIcon as DocumentMagnifyingGlassIconSolid,
  BellIcon as BellIconSolid,
  ShoppingCartIcon as ShoppingCartIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  TruckIcon as TruckIconSolid,
  BuildingOffice2Icon as BuildingOffice2IconSolid,
} from '@heroicons/react/24/solid';
import './NavItem.scss';

interface NavItemProps {
  path: string;
  label: string;
  icon: string;
  isCollapsed?: boolean;
  isMobile?: boolean;
}

// Icon mapping for outline and solid variants
// Includes icons for all portals: Customer, Case Manager, Compliance, Admin
const iconMap: Record<string, { outline: React.ElementType; solid: React.ElementType }> = {
  // Common
  HomeIcon: { outline: HomeIconOutline, solid: HomeIconSolid },
  DocumentTextIcon: { outline: DocumentTextIconOutline, solid: DocumentTextIconSolid },
  UserCircleIcon: { outline: UserCircleIconOutline, solid: UserCircleIconSolid },
  Cog6ToothIcon: { outline: Cog6ToothIconOutline, solid: Cog6ToothIconSolid },

  // Admin
  UsersIcon: { outline: UsersIconOutline, solid: UsersIconSolid },
  TagIcon: { outline: TagIconOutline, solid: TagIconSolid },
  ClipboardDocumentListIcon: { outline: ClipboardDocumentListIconOutline, solid: ClipboardDocumentListIconSolid },
  ChartBarIcon: { outline: ChartBarIconOutline, solid: ChartBarIconSolid },
  ChartBarSquareIcon: { outline: ChartBarSquareIconOutline, solid: ChartBarSquareIconSolid },
  BuildingOffice2Icon: { outline: BuildingOffice2IconOutline, solid: BuildingOffice2IconSolid },

  // Case Manager
  Squares2X2Icon: { outline: Squares2X2IconOutline, solid: Squares2X2IconSolid },
  ChatBubbleLeftRightIcon: { outline: ChatBubbleLeftRightIconOutline, solid: ChatBubbleLeftRightIconSolid },
  FolderIcon: { outline: FolderIconOutline, solid: FolderIconSolid },

  // Compliance
  QueueListIcon: { outline: QueueListIconOutline, solid: QueueListIconSolid },
  ShieldCheckIcon: { outline: ShieldCheckIconOutline, solid: ShieldCheckIconSolid },
  DocumentMagnifyingGlassIcon: { outline: DocumentMagnifyingGlassIconOutline, solid: DocumentMagnifyingGlassIconSolid },

  // Admin — Notifications
  BellIcon: { outline: BellIconOutline, solid: BellIconSolid },

  // Customer
  ShoppingCartIcon: { outline: ShoppingCartIconOutline, solid: ShoppingCartIconSolid },
  CreditCardIcon: { outline: CreditCardIconOutline, solid: CreditCardIconSolid },

  // Operations — Delivery dashboard
  TruckIcon: { outline: TruckIconOutline, solid: TruckIconSolid },
};

export const NavItem: React.FC<NavItemProps> = ({
  path,
  label,
  icon,
  isCollapsed = false,
  isMobile = false,
}) => {
  const location = useLocation();
  const isActive = location.pathname === path || location.pathname.startsWith(`${path}/`);

  const iconComponents = iconMap[icon];
  const IconComponent = iconComponents
    ? (isActive ? iconComponents.solid : iconComponents.outline)
    : HomeIconOutline;

  const classNames = [
    'nav-item',
    isActive && 'nav-item--active',
    isCollapsed && 'nav-item--collapsed',
    isMobile && 'nav-item--mobile',
  ].filter(Boolean).join(' ');

  return (
    <Link
      to={path}
      className={classNames}
      aria-current={isActive ? 'page' : undefined}
    >
      <IconComponent className="nav-item__icon" aria-hidden="true" />
      {!isCollapsed && <span className="nav-item__label">{label}</span>}
    </Link>
  );
};

export default NavItem;
